import { FileText, Folder, FileCheck, Sheet } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import StatsCard from "@/components/dashboard/stats-card";
import { IDocumentStats } from "@/types/documento";

const iconMap = {
  FileText,
  Folder,
  FileCheck,
  Sheet,
};

interface DocumentosStatsSectionProps {
  stats: IDocumentStats[];
  isLoading?: boolean;
}

export function DocumentosStatsSection({ stats, isLoading }: DocumentosStatsSectionProps) {
  // Badge variant mapping - based on percentage (negative = red)
  const getBadgeVariant = (stat: IDocumentStats): "positive" | "negative" => {
    // If totalSize contains percentage info, use percentage to determine variant
    if (stat.percentage < 0) {
      return "negative";
    }
    return "positive";
  };

  // Description mapping based on document type
  const getDescription = (stat: IDocumentStats): string => {
    if (stat.fileCount === 0 && stat.percentage > 0) {
      return "Em progresso no mês";
    }
    return `${stat.fileCount} Arquivos`;
  };

  // Loading skeleton state
  if (isLoading) {
    return (
      <div className="flex items-center gap-[32px]">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-[224px] w-[359px] rounded-[20px]" />
        ))}
      </div>
    );
  }

  // Empty state
  if (!stats || stats.length === 0) {
    return (
      <div className="flex items-center gap-[32px]">
        <div className="bg-[#15203c] rounded-[20px] px-[30px] py-0 h-[224px] flex items-center justify-center w-full">
          <p className="text-[#888888] text-[14px] font-['Plus_Jakarta_Sans']">
            Nenhum documento encontrado. Envie seu primeiro documento.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-[32px]">
      {stats.map((stat) => {
        const IconComponent = iconMap[stat.icon as keyof typeof iconMap] || FileText;
        return (
          <StatsCard
            key={stat.type}
            variant="document"
            icon={IconComponent}
            categoryLabel="Documentos"
            label={stat.label}
            value={stat.label}
            description={getDescription(stat)}
            badgeValue={stat.totalSize}
            badgeVariant={getBadgeVariant(stat)}
          />
        );
      })}
    </div>
  );
}
